import { useEffect, useState } from "react";
import { ArrowRightIcon, RefreshIcon } from "./icons";
import { localizedConferenceName } from "./conference-names";
import {
  type PortfolioConferenceSummary,
  type PortfolioOverview,
  type PortfolioOverviewClient,
} from "./portfolio-overview-api";
import {
  PORTFOLIO_ACCESS_ERROR_MESSAGES,
  portfolioFailureState,
  type PortfolioFailureState,
} from "./portfolio-api-errors";
import type { UserRole } from "./auth-types";
import { ConferenceBrandImage } from "./ConferenceBrandImage";
import { RiyalAmount } from "./RiyalAmount";
import {
  isolateText,
  useLocale,
  type AppLocale,
  type LocaleContextValue,
} from "./localization";

interface PortfolioOverviewPanelProps {
  readonly client: PortfolioOverviewClient;
  readonly role: UserRole;
}

export type OverviewState =
  | { readonly kind: "loading" }
  | { readonly kind: "loaded"; readonly overview: PortfolioOverview }
  | PortfolioFailureState;

type Translate = LocaleContextValue["t"];

function relativeTime(
  value: string | null,
  locale: AppLocale,
  t: Translate,
): string {
  if (!value) return t("sync.notSynchronized");
  const elapsed = Date.now() - Date.parse(value);
  if (!Number.isFinite(elapsed) || elapsed < 0) return t("sync.recently");
  const minutes = Math.max(1, Math.floor(elapsed / 60_000));
  const relativeFormat = new Intl.RelativeTimeFormat(`${locale}-u-nu-latn`, {
    numeric: "always",
    style: "short",
  });
  if (minutes < 60) {
    return t("sync.synchronizedRelative", {
      relative: relativeFormat.format(-minutes, "minute"),
    });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t("sync.synchronizedRelative", {
      relative: relativeFormat.format(-hours, "hour"),
    });
  }
  return t("sync.synchronizedRelative", {
    relative: relativeFormat.format(-Math.floor(hours / 24), "day"),
  });
}

function LifecycleLabel({
  value,
}: {
  readonly value: PortfolioConferenceSummary["lifecycle"];
}) {
  const { t } = useLocale();
  const labels = {
    active: t("conference.lifecycle.active"),
    upcoming: t("conference.lifecycle.upcoming"),
    ended: t("conference.lifecycle.ended"),
  } as const;
  return (
    <span className={`lifecycle-label lifecycle-label--${value}`}>
      <span aria-hidden="true" />
      {labels[value]}
    </span>
  );
}

function MetricRail({ overview }: { readonly overview: PortfolioOverview }) {
  const { number, t } = useLocale();
  const formatMoney = (cents: number) => <RiyalAmount minor={cents} />;
  const formatCount = (value: number | null) =>
    value === null ? t("common.unavailable") : number(value);
  const values = [
    {
      id: "total-conferences",
      label: t("metric.totalConferences"),
      value: formatCount(overview.metrics.totalConferences),
      definition: t("metric.totalConferences.definition"),
      mobileOrder: 1,
    },
    {
      id: "active-conferences",
      label: t("metric.activeConferences"),
      value: formatCount(overview.metrics.activeConferences),
      definition: t("metric.activeConferences.definition"),
      accent: true,
    },
    {
      id: "total-registrations",
      label: t("metric.totalRegistrations"),
      value: formatCount(overview.metrics.totalRegistrations),
      definition: t("metric.totalRegistrations.definition"),
      mobileOrder: 2,
    },
    {
      id: "net-collected",
      label: t("metric.netCollected"),
      value: formatMoney(overview.metrics.netCollectedCents),
      definition: t("metric.netCollected.definition"),
      mobileOrder: 3,
    },
    {
      id: "outstanding-invoices",
      label: t("metric.outstandingInvoices"),
      value: formatMoney(overview.metrics.outstandingInvoicesCents),
      definition: t("metric.outstandingInvoices.definition"),
    },
    {
      id: "sponsorship-revenue",
      label: t("metric.sponsorshipRevenue"),
      value: formatMoney(overview.metrics.sponsorshipRevenueCents),
      definition: t("metric.sponsorshipRevenue.definition"),
    },
    {
      id: "other-revenue",
      label: t("metric.otherRevenue"),
      value: formatMoney(overview.metrics.otherRevenueCents),
      definition: t("metric.otherRevenue.definition"),
    },
    {
      id: "visitors-this-month",
      label: t("metric.visitorsThisMonth"),
      value: formatCount(overview.metrics.visitorsThisMonth),
      definition: t("metric.visitorsThisMonth.definition"),
    },
  ];

  return (
    <section
      className="metric-rail"
      aria-label={t("overview.metrics.ariaLabel")}
    >
      {values.map((metric) => (
        <div
          className={
            metric.mobileOrder
              ? `metric-rail__item metric-rail__item--mobile-priority metric-rail__item--mobile-order-${metric.mobileOrder}`
              : "metric-rail__item"
          }
          key={metric.id}
          title={metric.definition}
        >
          <span>{metric.label}</span>
          <strong
            className={metric.accent ? "metric-value--accent" : undefined}
          >
            <bdi>{metric.value}</bdi>
          </strong>
        </div>
      ))}
    </section>
  );
}

function trendPoints(overview: PortfolioOverview): string {
  const values = overview.visitorTrend.map(
    ({ visitorsThisMonth }) => visitorsThisMonth,
  );
  if (values.length === 0) return "";
  const maximum = Math.max(...values);
  const minimum = Math.min(...values);
  const spread = Math.max(maximum - minimum, 1);
  return values
    .map((value, index) => {
      const x = values.length === 1 ? 320 : (index / (values.length - 1)) * 640;
      const y = 105 - ((value - minimum) / spread) * 90;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function VisitorTrend({ overview }: { readonly overview: PortfolioOverview }) {
  const { number, t } = useLocale();
  if (
    overview.analyticsCoverage === "unavailable" ||
    overview.metrics.visitorsThisMonth === null
  ) {
    return (
      <section
        className="visitor-trend visitor-trend--unavailable"
        aria-labelledby="visitor-trend-title"
      >
        <div>
          <h2 id="visitor-trend-title">{t("overview.visitors.title")}</h2>
          <p>{t("overview.visitors.unavailable")}</p>
        </div>
        <span>{t("overview.analytics.estimateNotice")}</span>
      </section>
    );
  }
  return (
    <section className="visitor-trend" aria-labelledby="visitor-trend-title">
      <div>
        <h2 id="visitor-trend-title">{t("overview.visitors.title")}</h2>
        <p>
          {overview.analyticsCoverage === "partial"
            ? t("overview.analytics.partialCoverage")
            : t("overview.analytics.last30Days")}
        </p>
      </div>
      {overview.visitorTrend.length > 0 ? (
        <svg
          viewBox="0 0 640 120"
          role="img"
          aria-label={t("overview.visitors.trendAriaLabel")}
        >
          <polyline points={trendPoints(overview)} />
        </svg>
      ) : (
        <p className="visitor-trend__history-empty">
          {t("overview.visitors.historyAccumulating")}
        </p>
      )}
      <strong>
        <bdi>
          {overview.metrics.visitorsThisMonth === null
            ? t("common.unavailable")
            : number(overview.metrics.visitorsThisMonth)}
        </bdi>
      </strong>
      <span>{t("metric.visitorsThisMonth")}</span>
      <small>{t("overview.analytics.estimateNotice")}</small>
    </section>
  );
}

function ConferenceTable({
  overview,
}: {
  readonly overview: PortfolioOverview;
}) {
  const { date, dateRange, locale, number, t } = useLocale();
  const formatCount = (value: number | null) =>
    value === null ? t("common.unavailable") : number(value);
  const formatMoney = (value: number | null) => <RiyalAmount minor={value} />;
  return (
    <section
      className="performance-section"
      aria-labelledby="performance-title"
    >
      <div className="section-heading">
        <div>
          <h2 id="performance-title">{t("overview.performance.title")}</h2>
          <p>{t("overview.performance.description")}</p>
        </div>
      </div>
      <div className="performance-table-wrap">
        <table className="performance-table">
          <caption>{t("overview.performance.caption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("table.conference")}</th>
              <th scope="col">{t("table.registrations")}</th>
              <th scope="col">{t("table.collected")}</th>
              <th scope="col">{t("table.visitors")}</th>
              <th scope="col">{t("table.lastSynchronized")}</th>
              <th scope="col">
                <span className="visually-hidden">{t("table.action")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {overview.conferences.map((conference) => (
              <tr key={conference.conferenceId}>
                <td data-label={t("table.conference")}>
                  <div className="conference-cell">
                    <ConferenceBrandImage
                      conferenceId={conference.conferenceId}
                      conferenceName={localizedConferenceName(
                        conference,
                        locale,
                      )}
                      configuredLogo={conference.iconUrl ?? conference.logoUrl}
                      compact
                      fallbackClassName="conference-monogram"
                      width="40"
                      height="40"
                    />
                    <div>
                      <strong>
                        <bdi>{localizedConferenceName(conference, locale)}</bdi>
                      </strong>
                      <span>
                        <bdi>
                          {dateRange(conference.startsAt, conference.endsAt, {
                            timeZone: "Asia/Riyadh",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </bdi>
                      </span>
                    </div>
                    <LifecycleLabel value={conference.lifecycle} />
                  </div>
                </td>
                <td
                  className="numeric-cell"
                  data-label={t("table.registrations")}
                >
                  <bdi>{formatCount(conference.registrationsTotal)}</bdi>
                </td>
                <td className="numeric-cell" data-label={t("table.collected")}>
                  <bdi>{formatMoney(conference.netCollectedCents)}</bdi>
                </td>
                <td className="numeric-cell" data-label={t("table.visitors")}>
                  <bdi>{formatCount(conference.visitorsThisMonth)}</bdi>
                </td>
                <td data-label={t("table.lastSynchronized")}>
                  <span
                    className={
                      conference.freshness === "fresh"
                        ? "freshness-text"
                        : "freshness-text freshness-text--stale"
                    }
                    title={
                      conference.lastSuccessfulSyncAt
                        ? isolateText(
                            date(conference.lastSuccessfulSyncAt, {
                              timeZone: "Asia/Riyadh",
                              dateStyle: "medium",
                              timeStyle: "short",
                            }),
                          )
                        : t("sync.noSuccessfulSynchronization")
                    }
                  >
                    {conference.freshness === "fresh"
                      ? relativeTime(conference.lastSuccessfulSyncAt, locale, t)
                      : t("sync.dataMayBeDelayed")}
                  </span>
                </td>
                <td data-label={t("table.action")}>
                  <a
                    className="table-action"
                    href={`#/conferences/${conference.conferenceId}`}
                  >
                    {t("overview.performance.viewConference")}
                    <ArrowRightIcon />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LoadingOverview() {
  const { t } = useLocale();
  return (
    <div
      className="overview-loading"
      aria-label={t("overview.loading")}
      aria-busy="true"
    >
      <div className="loading-rail loading-rail--overview">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} />
        ))}
      </div>
      <div className="loading-table">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export function PortfolioOverviewView({
  state,
  role,
  onRetry,
}: {
  readonly state: OverviewState;
  readonly role: UserRole;
  readonly onRetry: () => void;
}) {
  const { date, locale, t } = useLocale();
  if (state.kind === "loading") return <LoadingOverview />;
  if (state.kind === "error") {
    return (
      <section className="overview-state" role="alert">
        <h2>
          {state.sessionExpired
            ? t("session.expired.title")
            : state.accessError
              ? t("access.denied.title")
              : t("overview.error.title")}
        </h2>
        <p>
          {state.sessionExpired
            ? t("session.expired.description")
            : state.accessError
              ? t(PORTFOLIO_ACCESS_ERROR_MESSAGES[state.accessError])
              : t("overview.error.description")}
        </p>
        {!state.sessionExpired && !state.accessError ? (
          <button
            className="button button--secondary overview-state__action"
            type="button"
            onClick={onRetry}
          >
            <RefreshIcon />
            {t("common.tryAgain")}
          </button>
        ) : null}
      </section>
    );
  }

  const { overview } = state;
  if (overview.metrics.totalConferences === 0) {
    return (
      <section className="overview-state">
        <h2>{t("overview.empty.title")}</h2>
        <p>
          {role === "admin"
            ? t("overview.empty.adminDescription")
            : t("overview.empty.viewerDescription")}
        </p>
      </section>
    );
  }

  return (
    <>
      <div className="overview-source-line">
        <span
          title={
            overview.sourceSynchronizedAt
              ? isolateText(
                  date(overview.sourceSynchronizedAt, {
                    timeZone: "Asia/Riyadh",
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                )
              : t("sync.noSuccessfulSynchronization")
          }
        >
          {relativeTime(overview.sourceSynchronizedAt, locale, t)}
        </span>
        <span className={`source-state source-state--${overview.freshness}`}>
          <span aria-hidden="true" />
          {overview.freshness === "fresh"
            ? t("sync.sourcesFresh")
            : t("sync.sourceDelay")}
        </span>
      </div>
      <MetricRail overview={overview} />
      {overview.freshness === "partially_stale" ? (
        <p className="portfolio-warning" role="status">
          {t("overview.sourceDelayWarning")}
        </p>
      ) : null}
      <ConferenceTable overview={overview} />
      <VisitorTrend overview={overview} />
    </>
  );
}

export function PortfolioOverviewPanel({
  client,
  role,
}: PortfolioOverviewPanelProps) {
  const [state, setState] = useState<OverviewState>({ kind: "loading" });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const abortController = new AbortController();
    setState({ kind: "loading" });
    void client
      .load(abortController.signal)
      .then((overview) => setState({ kind: "loaded", overview }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setState(portfolioFailureState(error));
      });
    return () => abortController.abort();
  }, [client, revision]);

  return (
    <PortfolioOverviewView
      state={state}
      role={role}
      onRetry={() => setRevision((value) => value + 1)}
    />
  );
}
